'use client';

import { Link } from '@/components/link';
import { formatPricePerUmLine } from '@/components/product-utils/compute-product-price-weight';
import { formatCurrency } from '@/lib/currency';
import { formatMultipackWeightText, type MultipackOption } from '@/lib/multipack';
import { getProductTileUnitRow } from '@/lib/product-attributes-pim';
import { cn } from '@/lib/utils';
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

type MultipackSelectorProps = {
    options: MultipackOption[];
    currency: string;
};

/**
 * Equal-width multipack pack tiles (Figma): pack size label, total price, optional €/um.
 * Navigates via Link to unit vs multipack PDP (same pattern as wine vintage).
 * Pack label is quantity × base eti weight (e.g. `330g`, `3 x 330g`).
 */
export default function MultipackSelector({ options, currency }: MultipackSelectorProps): ReactElement | null {
    const { t, i18n } = useTranslation('product');

    if (options.length === 0) {
        return null;
    }

    const ariaLabel = t('multipack.ariaLabel');
    // Weight + UM live on the unit SKU; multipack bundle responses omit c_pim*.
    const unitProduct = options.find((o) => o.packQty === 1)?.product ?? options[0]?.product;
    const baseWeightText = getProductTileUnitRow(unitProduct ?? {}).weightText;

    return (
        <div
            className="flex w-full min-w-0 gap-2 order-1"
            role="radiogroup"
            aria-label={ariaLabel}
            data-testid="multipack-selector">
            {options.map((option) => {
                const packLabel = formatMultipackWeightText(baseWeightText, option.packQty) ?? '';
                const priceLabel =
                    option.price != null ? formatCurrency(option.price, i18n.language, currency) : undefined;
                const unitRow = getProductTileUnitRow(unitProduct ?? option.product, {
                    effectiveUnitPrice: option.price,
                    packQty: option.packQty,
                });
                const perUnitLabel = formatPricePerUmLine(unitRow, i18n.language, currency);
                const isSelected = option.selected;
                const isDisabled = option.orderable === false;
                const tileClassName = cn(
                    'flex flex-1 basis-0 min-w-0 flex-col items-center justify-center gap-0.5 rounded px-3 py-3 border border-solid text-center',
                    isSelected ? 'border-brown-20' : 'border-neutral-80',
                    isDisabled && !isSelected && 'opacity-50 cursor-not-allowed',
                    !isDisabled && 'cursor-pointer'
                );

                const content = (
                    <>
                        <span className="body-copy-l-bold text-neutral-10 whitespace-nowrap">{packLabel}</span>
                        {priceLabel ? (
                            <span className="body-copy-m text-neutral-10 whitespace-nowrap">{priceLabel}</span>
                        ) : null}
                        {perUnitLabel ? (
                            <span className="body-copy-s text-muted-foreground whitespace-nowrap">{perUnitLabel}</span>
                        ) : null}
                    </>
                );

                if (isDisabled) {
                    return (
                        <span
                            key={option.productId}
                            role="radio"
                            aria-checked={isSelected}
                            aria-disabled="true"
                            aria-label={packLabel}
                            data-testid={`multipack-${option.packQty}`}
                            data-selected={isSelected || undefined}
                            className={tileClassName}>
                            {content}
                        </span>
                    );
                }

                return (
                    <Link
                        key={option.productId}
                        to={option.href}
                        preventScrollReset
                        role="radio"
                        aria-checked={isSelected}
                        aria-label={packLabel}
                        data-testid={`multipack-${option.packQty}`}
                        data-selected={isSelected || undefined}
                        className={tileClassName}>
                        {content}
                    </Link>
                );
            })}
        </div>
    );
}
